import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, ShieldCheck, Users, Clock, MapPin, HeartPulse,
  Activity, Calculator, Check, Star, Quote, Phone, Mail, Ambulance, CalendarDays, X, Globe, MessageSquare, Send,
} from "lucide-react";
import { PageHero } from "./SiteLayout";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const ICONS = {
  shield: ShieldCheck, users: Users, clock: Clock, map: MapPin, heart: HeartPulse,
  activity: Activity, calculator: Calculator, star: Star, check: Check, phone: Phone,
  mail: Mail, ambulance: Ambulance,
};

const DynIcon = ({ name, ...props }) => {
  const C = ICONS[name] || ShieldCheck;
  return <C {...props} />;
};

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

const BTN_CLS = {
  primary: "btn btn-primary",
  outline: "btn btn-outline",
  dark: "btn btn-dark",
  light: "btn btn-light",
  white: "btn bg-[#00D2C2] text-[#0B0F14] font-bold hover:bg-[#14B8A6] shadow-lg",
};

const TONES = {
  white: "bg-[#0B0F14] border-t border-slate-800/60",
  light: "bg-[#121820]",
  dark: "bg-[#1A232E] border-t border-slate-800/80",
  teal: "bg-[#0F2B32] border-t border-[#00D2C2]/20",
};
const toneCls = (s, def = "white") => TONES[s.tone || (s.dark ? "dark" : def)] || TONES.white;
const darkTone = () => true;

function Btn({ b, testid }) {
  if (!b || !b.label) return null;
  const style = BTN_CLS[b.style] ? b.style : "primary";
  const icon = style === "outline" ? null : style === "primary" || style === "white" ? <ArrowUpRight size={18} /> : <ArrowRight size={18} />;
  return (
    <Link to={b.to || "/intake"} className={BTN_CLS[style]} data-testid={b.tid || testid}>
      {b.label} {icon}
    </Link>
  );
}

function Buttons({ buttons = [], testid, center }) {
  const list = (buttons || []).filter((b) => b && b.label);
  if (!list.length) return null;
  return (
    <div className={`mt-8 flex flex-wrap gap-4 ${center ? "justify-center" : ""}`}>
      {list.map((b, i) => <Btn key={i} b={b} testid={`${testid}-btn-${i}`} />)}
    </div>
  );
}

function SectionHead({ eyebrow, heading, subtitle, centered, dark }) {
  if (!eyebrow && !heading && !subtitle) return null;
  return (
    <div className={`reveal ${centered ? "text-center max-w-2xl mx-auto" : "max-w-2xl"}`}>
      {eyebrow && <span className={`eyebrow ${dark ? "!text-teal-300" : ""}`}>{eyebrow}</span>}
      {heading && <h2 className={`section-title mt-4 ${dark ? "!text-white" : ""}`}>{heading}</h2>}
      {subtitle && <p className={`mt-4 leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>{subtitle}</p>}
    </div>
  );
}

function HeroSection({ s }) {
  const slides = s.images?.length ? s.images : ["/site-assets/hero1.jpg"];
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    setSlide(0);
    const t = setInterval(() => setSlide((x) => (x + 1) % slides.length), 4000);
    return () => clearInterval(t);
  }, [slides.length]);
  const stats = s.stats || [];
  return (
    <section className="relative overflow-hidden -mt-[73px] pt-[73px] bg-[#0B0F14]" data-testid="site-hero">
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#00D2C2]/10 blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-[#10B981]/10 blur-3xl pointer-events-none" />
      <div className="container-x relative z-10 pt-16 pb-14 md:pt-24 md:pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          {s.eyebrow && (
            <span className="site-hero-in inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white bg-white/10 border border-white/25 rounded-full px-4 py-2 backdrop-blur-md shadow-lg">
              <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
              {s.eyebrow}
            </span>
          )}
          <h1 className="font-serif-site mt-6 text-white text-[2.6rem] leading-[1.05] sm:text-[3.6rem] font-bold tracking-tight site-hero-in site-hero-in-1">
            {s.title}
          </h1>
          {s.subtitle && (
            <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed site-hero-in site-hero-in-2">{s.subtitle}</p>
          )}
          <div className="mt-9 flex flex-wrap items-center gap-4 site-hero-in site-hero-in-3">
            <Btn b={s.primary_btn} testid="site-hero-quote" />
            <Btn b={s.secondary_btn} testid="site-hero-services" />
          </div>
        </div>
        <div className="relative site-hero-in site-hero-in-2">
          <div className="absolute -inset-3 rounded-[28px] border border-[#00D2C2]/25 pointer-events-none" />
          <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl aspect-[4/3] lg:aspect-[5/4]">
            {slides.map((src, i) => (
              <img key={`${src}-${i}`} src={src} alt="Event medical cover"
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ${i === slide ? "opacity-100 kenburns" : "opacity-0"}`} />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/60 via-transparent to-transparent" />
          </div>
          {slides.length > 1 && (
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-[#121820]/90 border border-white/10 rounded-full px-4 py-2.5 backdrop-blur-md shadow-xl">
              {slides.map((_, i) => (
                <button key={i} aria-label={`Slide ${i + 1}`} onClick={() => setSlide(i)} data-testid={`site-slide-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all ${i === slide ? "w-8 bg-[#00D2C2]" : "w-3 bg-white/30 hover:bg-white/60"}`} />
              ))}
            </div>
          )}
        </div>
      </div>
      {stats.length > 0 && (
        <div className="relative z-10 border-t border-slate-800/80 bg-[#121820]" data-testid="hero-stats">
          <div className="container-x grid grid-cols-2 md:grid-cols-4">
            {stats.map((st, i) => (
              <div key={`${st.label}-${i}`} data-testid={`hero-stat-${i}`}
                className={`px-6 py-7 text-center md:text-left border-slate-800/80 hover:bg-[#00D2C2]/5 transition-colors group/stat ${i % 2 === 1 ? "border-l" : ""} ${i >= 2 ? "border-t md:border-t-0" : ""} ${i > 0 ? "md:border-l" : ""}`}>
                <div className="stat-mono text-3xl md:text-4xl font-extrabold text-white group-hover/stat:text-[#00D2C2] transition-colors">{st.value}</div>
                <div className="text-xs uppercase tracking-widest text-slate-400 mt-1.5 font-semibold">{st.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function TrustBar({ s, testid }) {
  const items = s.items || [];
  if (!items.length) return null;
  return (
    <div className="bg-[#0B0F14] border-y border-slate-800 overflow-hidden" data-testid={testid}>
      <div className="py-5 marquee text-sm text-slate-300 font-medium">
        {[...items, ...items].map((t, i) => (
          <span key={`${t}-${i}`} className="inline-flex items-center gap-2 shrink-0">
            <ShieldCheck size={16} className="text-[#00D2C2]" /> {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function ServicesGrid({ s, services, testid }) {
  const detailed = s.variant === "detailed";
  const list = services || [];
  return (
    <section className={`py-20 md:py-28 ${toneCls(s)}`} data-testid={testid}>
      <div className="container-x">
        {(s.eyebrow || s.heading || s.subtitle) && (
          <div className="reveal text-center max-w-2xl mx-auto mb-12">
            {s.eyebrow && <span className="eyebrow">{s.eyebrow}</span>}
            {s.heading && <h2 className="section-title mt-4">{s.heading}</h2>}
            {s.subtitle && <p className="mt-4 text-slate-300 leading-relaxed">{s.subtitle}</p>}
          </div>
        )}
        <div className={`grid md:grid-cols-2 gap-8 ${detailed ? "" : "max-w-5xl mx-auto"}`}>
          {list.map((svc, i) => (
            <div key={svc.slug || i} className={`reveal reveal-delay-${(i % 3) + 1}`}>
              {detailed ? (
                <div className="card-soft overflow-hidden hover:shadow-xl transition-shadow duration-300 h-full" data-testid={`services-card-${i}`}>
                  <div className="relative h-64 overflow-hidden">
                    {svc.image && <img src={svc.image} alt={svc.name} className="w-full h-full object-cover" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
                    <span className="absolute bottom-4 left-4 inline-flex w-12 h-12 rounded-xl bg-[#00D2C2] text-[#0B0F14] items-center justify-center shadow-lg">
                      <DynIcon name={svc.icon} size={24} />
                    </span>
                  </div>
                  <div className="p-7">
                    <h2 className="font-serif-site text-2xl font-extrabold text-white tracking-tight">{svc.name}</h2>
                    <p className="mt-3 text-slate-300 leading-relaxed">{svc.tagline}</p>
                    {(svc.points || []).length > 0 && (
                      <ul className="mt-5 space-y-2">
                        {svc.points.map((p) => (
                          <li key={p} className="flex items-start gap-2.5 text-sm text-slate-200">
                            <span className="mt-0.5 inline-flex w-5 h-5 rounded-full bg-[#00D2C2]/15 text-[#00D2C2] border border-[#00D2C2]/25 items-center justify-center shrink-0">
                              <ArrowRight size={12} />
                            </span>
                            {p}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-6 flex flex-wrap gap-3">
                      <Link to={`/services/${svc.slug}`} className="btn btn-outline !py-2.5" data-testid={`services-detail-${i}`}>Learn more</Link>
                      <Link to="/intake" className="btn btn-primary !py-2.5" data-testid={`services-quote-${i}`}>
                        Get a Quote <ArrowUpRight size={16} />
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <Link to={`/services/${svc.slug}`} className="group block card-soft overflow-hidden h-full hover:shadow-xl transition-shadow duration-300" data-testid={`site-service-card-${i}`}>
                  <div className="relative h-60 overflow-hidden">
                    {svc.image && <img src={svc.image} alt={svc.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
                    <span className="absolute bottom-4 left-4 inline-flex w-12 h-12 rounded-xl bg-[#00D2C2] text-[#0B0F14] items-center justify-center shadow-lg">
                      <DynIcon name={svc.icon} size={24} />
                    </span>
                  </div>
                  <div className="p-7">
                    <h3 className="text-xl font-bold text-white font-serif-site">{svc.name}</h3>
                    <p className="mt-3 text-slate-300 leading-relaxed">{svc.tagline}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-[#00D2C2] font-semibold group-hover:gap-3 transition-all">
                      Learn more <ArrowRight size={18} />
                    </span>
                  </div>
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TextSection({ s, testid }) {
  const dark = darkTone(s);
  return (
    <section className={`py-20 ${toneCls(s)}`} data-testid={testid}>
      <div className="container-x">
        <div className={s.centered ? "text-center max-w-2xl mx-auto" : "max-w-2xl"}>
          <SectionHead eyebrow={s.eyebrow} heading={s.heading} dark={dark} />
          {(s.paragraphs || []).filter(Boolean).map((p, i) => (
            <p key={i} className={`${i === 0 && !(s.eyebrow || s.heading) ? "" : "mt-4"} leading-relaxed ${dark ? "text-slate-300" : "text-slate-300"}`}>{p}</p>
          ))}
          <Buttons buttons={s.buttons} testid={testid} center={s.centered} />
        </div>
      </div>
    </section>
  );
}

function TextImageSection({ s, testid }) {
  const dark = darkTone(s);
  const imgRight = s.image_side !== "left";
  return (
    <section className={`py-20 md:py-28 ${toneCls(s)}`} data-testid={testid}>
      <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
        <div className={`reveal ${!imgRight ? "lg:order-2" : ""}`}>
          <SectionHead eyebrow={s.eyebrow} heading={s.heading} dark={dark} />
          {(s.paragraphs || []).filter(Boolean).map((p, i) => (
            <p key={i} className={`${i === 0 ? "mt-6" : "mt-4"} leading-relaxed ${dark ? "text-slate-300" : "text-slate-300"}`}>{p}</p>
          ))}
          {(s.bullets || []).filter(Boolean).length > 0 && (
            <ul className="mt-7 grid sm:grid-cols-2 gap-3">
              {s.bullets.filter(Boolean).map((b) => (
                <li key={b} className={`flex items-center gap-3 font-medium ${dark ? "text-slate-200" : "text-slate-200"}`}>
                  <span className="inline-flex w-6 h-6 rounded-full bg-[#10B981] items-center justify-center shrink-0">
                    <Check size={14} className="text-white" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          )}
          <Buttons buttons={s.buttons} testid={testid} />
        </div>
        {s.image && (
          <div className={`reveal reveal-delay-1 relative ${!imgRight ? "lg:order-1" : ""}`}>
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img src={s.image} alt={s.heading || ""} className="w-full h-[460px] object-cover" />
            </div>
            {s.badge?.value && (
              <div className="absolute -bottom-6 -left-6 bg-[#00D2C2] text-[#0B0F14] rounded-2xl p-6 shadow-xl hidden sm:block">
                <div className="text-3xl font-extrabold font-serif-site">{s.badge.value}</div>
                <div className="text-sm text-teal-100">{s.badge.label}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function FeaturesSection({ s, testid }) {
  return (
    <section className={`py-20 md:py-24 ${toneCls(s, "light")}`} data-testid={testid}>
      <div className="container-x">
        <SectionHead eyebrow={s.eyebrow} heading={s.heading} subtitle={s.subtitle} dark={darkTone(s)} />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {(s.items || []).map((f, i) => (
            <div key={i} className={`reveal reveal-delay-${(i % 3) + 1}`}>
              <div className="card-soft p-7 h-full hover:-translate-y-1.5 hover:shadow-xl hover:border-[#00D2C2]/40 transition-all duration-300">
                <span className="inline-flex w-12 h-12 rounded-xl bg-[#00D2C2]/15 text-[#00D2C2] border border-[#00D2C2]/25 items-center justify-center">
                  <DynIcon name={f.icon} size={24} />
                </span>
                <h3 className="mt-5 text-lg font-bold text-white font-serif-site">{f.title}</h3>
                <p className="mt-2.5 text-sm text-slate-300 leading-relaxed">{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function KitGridSection({ s, testid }) {
  return (
    <section className={`py-20 md:py-24 ${toneCls(s)}`} data-testid={testid}>
      <div className="container-x">
        <SectionHead eyebrow={s.eyebrow} heading={s.heading} subtitle={s.subtitle} dark={darkTone(s)} />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {(s.items || []).map((f, i) => (
            <div key={i} className="card-soft overflow-hidden card-lift" data-testid={`${testid}-item-${i}`}>
              {f.image && <img src={f.image} alt={f.title || "Kit"} className="w-full h-44 object-cover" loading="lazy" />}
              <div className="p-6">
                <h3 className="text-lg font-bold text-white font-serif-site">{f.title}</h3>
                {f.text && <p className="mt-2 text-sm text-slate-300 leading-relaxed">{f.text}</p>}
                {f.more && (
                  <a href={`#detail-${f.more}`} className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#00D2C2] hover:underline" data-testid={`${testid}-more-${i}`}>
                    More info & photos ↓
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PartnersSection({ s, testid }) {
  const [msgFor, setMsgFor] = useState(null);
  const [mform, setMform] = useState({ name: "", email: "", message: "" });
  const [mstate, setMstate] = useState({ sending: false, sent: false, error: "" });
  const chip = "inline-flex items-center gap-1.5 text-xs font-bold text-slate-200 border border-slate-600 rounded-full px-3 py-1.5 hover:border-[#00D2C2] hover:text-[#00D2C2] transition-colors";
  const openMsg = (p) => { setMsgFor(p); setMform({ name: "", email: "", message: "" }); setMstate({ sending: false, sent: false, error: "" }); };
  const sendMsg = async () => {
    if (!mform.name.trim() || !mform.email.includes("@") || !mform.message.trim()) {
      setMstate((x) => ({ ...x, error: "Please fill in your name, email and message" }));
      return;
    }
    setMstate({ sending: true, sent: false, error: "" });
    try {
      const res = await fetch(`${BACKEND}/api/public/partner-message`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ partner: msgFor.name || "", ...mform }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Couldn't send right now — please try again shortly");
      setMstate({ sending: false, sent: true, error: "" });
    } catch (e) {
      setMstate({ sending: false, sent: false, error: e.message });
    }
  };
  const fld = "w-full rounded-lg border border-slate-600 bg-[#121820] px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#00D2C2]";
  return (
    <section className={`py-20 md:py-24 ${toneCls(s)}`} data-testid={testid}>
      <div className="container-x">
        <SectionHead eyebrow={s.eyebrow} heading={s.heading} subtitle={s.subtitle} dark={darkTone(s)} centered />
        <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {(s.items || []).map((p, i) => (
            <div key={i} className="card-soft p-7 flex flex-col" data-testid={`${testid}-item-${i}`}>
              <div className="flex items-start gap-4">
                {p.image ? (
                  <img src={p.image} alt={p.name} className="h-16 w-16 rounded-2xl object-cover border border-slate-700 shrink-0" data-testid={`${testid}-img-${i}`} />
                ) : (
                  <div className="h-16 w-16 rounded-2xl bg-[#00D2C2]/10 border border-[#00D2C2]/25 grid place-items-center text-2xl font-extrabold text-[#00D2C2] shrink-0">
                    {(p.name || "?").trim().charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  {p.category && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#00D2C2]/15 text-[#00D2C2] border border-[#00D2C2]/25 px-2.5 py-1 rounded-full">{p.category}</span>}
                  <h3 className="mt-2 text-xl font-bold text-white font-serif-site">{p.name}</h3>
                </div>
              </div>
              {p.blurb && <p className="mt-4 text-sm text-slate-300">{p.blurb}</p>}
              {p.offers && (
                <div className="mt-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">What they offer</p>
                  <ul className="space-y-1">
                    {p.offers.split("\n").filter(Boolean).map((o, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-300" data-testid={`${testid}-offer-${i}-${j}`}><Check size={13} className="text-[#10B981] mt-0.5 shrink-0" />{o}</li>
                    ))}
                  </ul>
                </div>
              )}
              {p.why && <p className="mt-3 text-sm italic text-slate-400 border-l-2 border-[#00D2C2]/40 pl-3">&ldquo;{p.why}&rdquo;</p>}
              {p.review && (
                <div className="mt-3 rounded-xl border border-amber-500/25 bg-amber-500/5 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-400/80 mb-1">Our review</p>
                  <p className="text-sm text-slate-300" data-testid={`${testid}-review-${i}`}>{p.review}</p>
                </div>
              )}
              {p.info && <p className="mt-3 text-xs text-slate-400" data-testid={`${testid}-info-${i}`}>{p.info}</p>}
              <div className="mt-5 flex flex-wrap gap-2">
                {p.phone && (
                  <a href={`tel:${p.phone}`} data-testid={`${testid}-call-${i}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#005EB8] hover:bg-[#004a93] rounded-full px-4 py-2 transition-colors">
                    <Phone size={13} /> Call us
                  </a>
                )}
                {(p.email || "").trim() && (
                  <a href={`mailto:${p.email}`} data-testid={`${testid}-email-${i}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#00A499] hover:bg-[#008f85] rounded-full px-4 py-2 transition-colors">
                    <Mail size={13} /> Email us
                  </a>
                )}
                {(p.email || "").trim() && (
                  <button type="button" onClick={() => openMsg(p)} data-testid={`${testid}-msg-${i}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#00D2C2] border border-[#00D2C2]/50 hover:bg-[#00D2C2]/10 rounded-full px-4 py-2 transition-colors">
                    <MessageSquare size={13} /> Message us
                  </button>
                )}
                {p.website && (
                  <a href={p.website} target="_blank" rel="noreferrer" className={chip} data-testid={`${testid}-website-${i}`}><Globe size={13} /> Website</a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {msgFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={() => !mstate.sending && setMsgFor(null)} data-testid="partner-msg-modal" role="dialog" aria-modal="true">
          <div className="relative w-full max-w-md rounded-3xl border border-slate-700 bg-[#0F141B] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setMsgFor(null)} aria-label="Close" data-testid="partner-msg-close"
              className="absolute top-4 right-4 h-9 w-9 grid place-items-center rounded-full border border-slate-600 text-slate-300 hover:text-white hover:border-white transition-colors">
              <X size={16} />
            </button>
            <h3 className="text-xl font-bold text-white font-serif-site">Message {msgFor.name}</h3>
            <p className="mt-1 text-xs text-slate-400">Your details go straight to them — mention Wales Event Medical Services.</p>
            {mstate.sent ? (
              <div className="mt-5 rounded-xl border border-[#10B981]/40 bg-[#10B981]/10 p-4 text-sm text-[#34d399]" data-testid="partner-msg-sent">
                Message sent — they'll be in touch.
              </div>
            ) : (
              <div className="mt-5 space-y-3">
                <input value={mform.name} onChange={(e) => setMform((f) => ({ ...f, name: e.target.value }))} placeholder="Your name" className={fld} data-testid="partner-msg-name" />
                <input value={mform.email} onChange={(e) => setMform((f) => ({ ...f, email: e.target.value }))} placeholder="Your email" type="email" className={fld} data-testid="partner-msg-email" />
                <textarea rows={4} value={mform.message} onChange={(e) => setMform((f) => ({ ...f, message: e.target.value }))} placeholder="Your message" className={fld} data-testid="partner-msg-text" />
                {mstate.error && <p className="text-xs text-red-400" data-testid="partner-msg-error">{mstate.error}</p>}
                <button type="button" onClick={sendMsg} disabled={mstate.sending} data-testid="partner-msg-send"
                  className="w-full h-11 rounded-full bg-[#00D2C2] hover:bg-[#00b8aa] disabled:opacity-50 text-[#06201D] text-sm font-extrabold inline-flex items-center justify-center gap-2 transition-colors">
                  <Send size={15} /> {mstate.sending ? "Sending…" : "Send message"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function PriceCardsSection({ s, testid }) {
  const [openIdx, setOpenIdx] = useState(null);
  const items = s.items || [];
  const hasDetail = (p) =>
    (p.photos || []).some(Boolean) || (p.paragraphs || []).some(Boolean) ||
    (p.specs || []).some((x) => x.label || x.value) || (p.requests || []).some(Boolean);
  const openPkg = openIdx === null ? null : items[openIdx];
  return (
    <section className={`py-20 md:py-24 ${toneCls(s)}`} data-testid={testid}>
      <div className="container-x">
        <SectionHead eyebrow={s.eyebrow} heading={s.heading} subtitle={s.subtitle} dark={darkTone(s)} centered />
        <div className="mt-12 grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {items.map((p, i) => (
            <button type="button" key={i} onClick={() => hasDetail(p) && setOpenIdx(i)}
              className={`card-soft p-7 flex flex-col text-left transition-all duration-200 ${p.tag ? "border-[#00D2C2]/50 ring-1 ring-[#00D2C2]/30" : ""} ${hasDetail(p) ? "cursor-pointer hover:border-[#00D2C2]/60 hover:-translate-y-1" : "cursor-default"}`}
              data-testid={`${testid}-item-${i}`}>
              {p.tag && <span className="self-start text-[10px] font-bold uppercase tracking-wider bg-[#00D2C2]/15 text-[#00D2C2] border border-[#00D2C2]/25 px-2.5 py-1 rounded-full">{p.tag}</span>}
              <h3 className="mt-4 text-xl font-bold text-white font-serif-site">{p.name}</h3>
              <p className="mt-1 text-2xl font-extrabold text-[#00D2C2]">{p.price}</p>
              <ul className="mt-5 space-y-2.5">
                {(p.features || []).filter(Boolean).map((f, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-sm text-slate-300"><Check size={15} className="text-[#10B981] mt-0.5 shrink-0" />{f}</li>
                ))}
              </ul>
              {hasDetail(p) && (
                <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold text-[#00D2C2]" data-testid={`${testid}-more-${i}`}>
                  More photos & details <ArrowRight size={13} />
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      {openPkg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={() => setOpenIdx(null)} data-testid="pkg-modal" role="dialog" aria-modal="true">
          <div className="relative w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-3xl border border-slate-700 bg-[#0F141B] p-6 md:p-9 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpenIdx(null)} aria-label="Close package details" data-testid="pkg-modal-close"
              className="sticky top-0 float-right h-10 w-10 grid place-items-center rounded-full border border-slate-600 bg-[#0F141B] text-slate-300 hover:text-white hover:border-white transition-colors z-10">
              <X size={18} />
            </button>
            {openPkg.tag && <span className="inline-block text-[10px] font-bold uppercase tracking-wider bg-[#00D2C2]/15 text-[#00D2C2] border border-[#00D2C2]/25 px-2.5 py-1 rounded-full">{openPkg.tag}</span>}
            <h3 className="mt-3 text-3xl font-bold text-white font-serif-site">{openPkg.name}</h3>
            <p className="mt-1 text-2xl font-extrabold text-[#00D2C2]">{openPkg.price}</p>
            {(openPkg.photos || []).filter(Boolean).length > 0 && (
              <div className="mt-6 grid grid-cols-2 gap-3">
                {openPkg.photos.filter(Boolean).map((src, i) => (
                  <img key={i} src={src} alt={openPkg.name} loading="lazy" data-testid={`pkg-modal-photo-${i}`}
                    className={`rounded-2xl object-cover w-full ${i === 0 ? "col-span-2 h-56 md:h-72" : "h-36"}`} />
                ))}
              </div>
            )}
            {(openPkg.paragraphs || []).filter(Boolean).map((p, i) => (
              <p key={i} className="mt-4 leading-relaxed text-slate-300">{p}</p>
            ))}
            {(openPkg.features || []).filter(Boolean).length > 0 && (
              <div className="mt-6">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2.5">What's included</p>
                <ul className="space-y-2.5">
                  {openPkg.features.filter(Boolean).map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-slate-200"><Check size={15} className="text-[#10B981] mt-0.5 shrink-0" />{f}</li>
                  ))}
                </ul>
              </div>
            )}
            {(openPkg.specs || []).some((x) => x.label || x.value) && (
              <dl className="mt-6 grid sm:grid-cols-2 gap-3">
                {openPkg.specs.filter((x) => x.label || x.value).map((x, i) => (
                  <div key={i} className="rounded-xl border border-slate-700 bg-[#121820] px-4 py-3" data-testid={`pkg-modal-spec-${i}`}>
                    <dt className="text-[11px] font-bold uppercase tracking-wider text-[#00D2C2]">{x.label}</dt>
                    <dd className="mt-1 text-sm font-semibold text-white">{x.value}</dd>
                  </div>
                ))}
              </dl>
            )}
            {(openPkg.requests || []).filter(Boolean).length > 0 && (
              <div className="mt-6 rounded-xl border border-slate-700 bg-[#121820] p-5">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2.5">What we ask you to supply</p>
                <ul className="space-y-2">
                  {openPkg.requests.filter(Boolean).map((r, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-200" data-testid={`pkg-modal-req-${i}`}><Check size={15} className="text-[#10B981] mt-0.5 shrink-0" />{r}</li>
                  ))}
                </ul>
              </div>
            )}
            <Link to="/intake" className="btn btn-primary mt-8 inline-flex" data-testid="pkg-modal-quote" onClick={() => setOpenIdx(null)}>
              Get a quote for this package
            </Link>
          </div>
        </div>
      )}
    </section>
  );
}

function KitDetailSection({ s, testid }) {
  const photos = (s.photos || []).filter(Boolean);
  const specs = (s.specs || []).filter((x) => x.label || x.value);
  const requests = (s.requests || []).filter(Boolean);
  return (
    <section id={s.slug ? `detail-${s.slug}` : undefined} className={`py-20 md:py-24 ${toneCls(s)}`} data-testid={testid}>
      <div className="container-x">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            {s.eyebrow && <span className="eyebrow">{s.eyebrow}</span>}
            <h2 className="section-title mt-3">{s.heading}</h2>
            {(s.paragraphs || []).filter(Boolean).map((p, i) => (
              <p key={i} className="mt-4 leading-relaxed text-slate-300">{p}</p>
            ))}
            {specs.length > 0 && (
              <dl className="mt-6 grid sm:grid-cols-2 gap-3">
                {specs.map((x, i) => (
                  <div key={i} className="rounded-xl border border-slate-700 bg-[#121820] px-4 py-3" data-testid={`${testid}-spec-${i}`}>
                    <dt className="text-[11px] font-bold uppercase tracking-wider text-[#00D2C2]">{x.label}</dt>
                    <dd className="mt-1 text-sm font-semibold text-white">{x.value}</dd>
                  </div>
                ))}
              </dl>
            )}
            {requests.length > 0 && (
              <div className="mt-6 rounded-xl border border-slate-700 bg-[#121820] p-5">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2.5">What we ask you to supply</p>
                <ul className="space-y-2">
                  {requests.map((r, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-200" data-testid={`${testid}-req-${i}`}><Check size={15} className="text-[#10B981] mt-0.5 shrink-0" />{r}</li>
                  ))}
                </ul>
              </div>
            )}
            <Link to={`/intake?kit=${encodeURIComponent(s.slug || "")}`} className="btn btn-primary mt-8" data-testid={`${testid}-quote`}>
              Get a quote with this
            </Link>
          </div>
          {photos.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              {photos.map((src, i) => (
                <img key={i} src={src} alt={s.heading || "Kit"} loading="lazy"
                  className={`rounded-2xl object-cover w-full shadow-lg ${i === 0 ? "col-span-2 h-64 md:h-80" : "h-40"}`}
                  data-testid={`${testid}-photo-${i}`} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ChipsSection({ s, testid }) {
  const dark = darkTone(s);
  return (
    <section className={`py-20 ${toneCls(s, "light")}`} data-testid={testid}>
      <div className="container-x">
        <SectionHead eyebrow={s.eyebrow} heading={s.heading} subtitle={s.subtitle} dark={dark} />
        {(s.paragraphs || []).filter(Boolean).map((p, i) => (
          <p key={i} className={`mt-4 leading-relaxed max-w-2xl ${dark ? "text-slate-300" : "text-slate-300"}`}>{p}</p>
        ))}
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {(s.items || []).filter(Boolean).map((o) => (
            <div key={o} className="card-soft px-5 py-4 flex items-center gap-3" data-testid={`${testid}-item-${slugify(o)}`}>
              <span className="inline-flex w-7 h-7 rounded-full bg-[#00D2C2] text-[#0B0F14] items-center justify-center shrink-0">
                <Check size={14} />
              </span>
              <span className="text-sm font-semibold text-white">{o}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ChecklistSection({ s, testid }) {
  if (s.card) {
    return (
      <section className={`py-20 md:py-24 ${TONES.dark}`} data-testid={testid}>
        <div className="container-x grid lg:grid-cols-2 gap-14 items-start">
          <div className="reveal">
            <SectionHead eyebrow={s.eyebrow} heading={s.heading} dark />
            {(s.paragraphs || []).filter(Boolean).map((p, i) => (
              <p key={i} className={`${i === 0 ? "mt-6" : "mt-4"} text-slate-300 leading-relaxed`}>{p}</p>
            ))}
            <Buttons buttons={s.buttons} testid={testid} />
          </div>
          <div className="reveal reveal-delay-1 card-soft p-8">
            {s.list_title && <p className="text-xs font-bold uppercase tracking-widest text-[#00D2C2] mb-4">{s.list_title}</p>}
            <ul className="space-y-3">
              {(s.items || []).filter(Boolean).map((p) => (
                <li key={p} className="flex items-start gap-3 text-slate-200">
                  <span className="mt-0.5 inline-flex w-6 h-6 rounded-full bg-[#10B981] items-center justify-center shrink-0">
                    <Check size={13} className="text-white" />
                  </span>
                  <span className="text-sm font-medium">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    );
  }
  const t = s.tone || (s.dark === false ? "white" : "dark");
  const dark = t === "dark" || t === "teal";
  return (
    <section className={`py-20 md:py-24 ${TONES[t] || TONES.dark}`} data-testid={testid}>
      <div className={`container-x ${s.image ? "grid lg:grid-cols-2 gap-14 items-center" : ""}`}>
        <div className="reveal">
          <SectionHead eyebrow={s.eyebrow} heading={s.heading} dark={dark} />
          {(s.paragraphs || []).filter(Boolean).map((p, i) => (
            <p key={i} className={`${i === 0 ? "mt-6" : "mt-4"} leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>{p}</p>
          ))}
          {(s.items || []).filter(Boolean).length > 0 && (
            <ul className="mt-7 grid sm:grid-cols-2 gap-3">
              {s.items.filter(Boolean).map((p) => (
                <li key={p} className={`flex items-center gap-3 font-medium ${dark ? "text-slate-200" : "text-slate-700"}`}>
                  <span className="inline-flex w-6 h-6 rounded-full bg-[#10B981] items-center justify-center shrink-0">
                    <Check size={14} className="text-white" />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          )}
          <Buttons buttons={s.buttons} testid={testid} />
        </div>
        {s.image && (
          <div className="reveal reveal-delay-1">
            <img src={s.image} alt={s.heading || ""} className="w-full h-[440px] object-cover rounded-3xl shadow-2xl" />
          </div>
        )}
      </div>
    </section>
  );
}

function ColumnsSection({ s, testid }) {
  const dark = darkTone(s);
  return (
    <section className={`py-20 md:py-28 ${toneCls(s)}`} data-testid={testid}>
      <div className="container-x grid lg:grid-cols-2 gap-14">
        <div className="reveal">
          <SectionHead eyebrow={s.eyebrow} heading={s.heading} dark={dark} />
          {(s.paragraphs || []).filter(Boolean).map((p, i) => (
            <p key={i} className={`${i === 0 ? "mt-6" : "mt-4"} leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>{p}</p>
          ))}
          {s.note?.title && (
            <div className="mt-6 rounded-xl border-l-4 border-[#00D2C2] bg-[#00D2C2]/15 p-4" data-testid="about-cqc-note">
              <p className="text-sm font-bold text-white">{s.note.title}</p>
              {s.note.text && <p className="text-xs text-slate-300 mt-1 leading-relaxed">{s.note.text}</p>}
            </div>
          )}
          {(s.tags || []).filter(Boolean).length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {s.tags.filter(Boolean).map((t) => (
                <span key={t} className="px-3 py-1.5 rounded-full bg-[#00D2C2]/15 text-[#00D2C2] text-xs font-bold">{t}</span>
              ))}
            </div>
          )}
          <Buttons buttons={s.buttons} testid={testid} />
        </div>
        <div className="reveal reveal-delay-1 space-y-6">
          {(s.cards || []).map((c, i) => (
            <div key={i} className="card-soft p-7">
              <h3 className="text-lg font-semibold text-white">{c.heading}</h3>
              {c.text && <p className="mt-2 text-sm text-slate-300 leading-relaxed">{c.text}</p>}
              {(c.items || []).filter(Boolean).length > 0 && (
                <ul className="mt-4 space-y-2.5">
                  {c.items.filter(Boolean).map((it) => (
                    <li key={it} className="flex items-start gap-2.5 text-sm text-slate-200">
                      <span className="mt-0.5 inline-flex w-5 h-5 rounded-full bg-[#00D2C2] text-[#0B0F14] items-center justify-center shrink-0">
                        <Check size={12} />
                      </span>
                      {it}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ReviewsSection({ s, testid }) {
  const items = s.items || [];
  if (!items.length) return null;
  return (
    <section className={`py-20 md:py-28 ${toneCls(s, "light")}`} data-testid={testid}>
      <div className="container-x">
        <SectionHead eyebrow={s.eyebrow} heading={s.heading} subtitle={s.subtitle} centered dark={darkTone(s)} />
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {items.map((r, i) => (
            <div key={i} className={`reveal reveal-delay-${(i % 3) + 1}`}>
              <div className="card-soft p-8 h-full relative hover:shadow-xl transition-shadow duration-300">
                <Quote size={40} className="text-teal-100 absolute top-6 right-6" />
                <div className="flex gap-1 text-amber-400">
                  {[...Array(5)].map((_, st) => <Star key={st} size={18} className="fill-amber-400" />)}
                </div>
                <p className="mt-4 text-slate-200 leading-relaxed relative z-10">"{r.text}"</p>
                <div className="mt-6 flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full bg-[#00D2C2] text-[#0B0F14] flex items-center justify-center font-bold">
                    {(r.name || "?")[0]}
                  </span>
                  <div>
                    <div className="font-bold text-white">{r.name}</div>
                    <div className="text-sm text-slate-400">{r.role}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CalculatorPromo({ s, testid }) {
  return (
    <section className={`py-20 ${toneCls(s)}`} data-testid={testid}>
      <div className="container-x">
        <div className="reveal">
          <div className="card-soft overflow-hidden grid md:grid-cols-5">
            <div className="md:col-span-3 p-10 md:p-12">
              <span className="inline-flex w-14 h-14 rounded-2xl bg-[#00D2C2]/15 text-[#00D2C2] border border-[#00D2C2]/25 items-center justify-center">
                <Calculator size={28} />
              </span>
              {s.heading && <h2 className="section-title mt-6">{s.heading}</h2>}
              {s.text && <p className="mt-4 text-slate-300 leading-relaxed max-w-xl">{s.text}</p>}
              <div className="mt-8"><Btn b={s.button} testid="site-calculator-cta" /></div>
            </div>
            <div className="md:col-span-2 bg-[#0F2B32] p-10 border-l border-[#00D2C2]/20 flex flex-col justify-center gap-4">
              {(s.points || []).filter(Boolean).map((t) => (
                <div key={t} className="flex items-center gap-3 text-white">
                  <span className="inline-flex w-7 h-7 rounded-full bg-white/20 items-center justify-center shrink-0">
                    <Check size={15} />
                  </span>
                  <span className="text-teal-50 font-medium">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CtaSection({ s, testid }) {
  if (s.style === "plain") {
    return (
      <section className={`py-16 ${toneCls(s, "light")}`} data-testid={testid}>
        <div className="container-x text-center max-w-2xl mx-auto">
          {s.heading && <h2 className="section-title">{s.heading}</h2>}
          {s.text && <p className="mt-4 text-slate-300 leading-relaxed">{s.text}</p>}
          <Buttons buttons={s.buttons} testid={testid} center />
        </div>
      </section>
    );
  }
  return (
    <section className={`py-16 ${toneCls(s)}`} data-testid={testid}>
      <div className="container-x">
        <div className="rounded-3xl bg-gradient-to-br from-[#0F2B32] via-[#16202C] to-[#0B0F14] border border-[#00D2C2]/25 px-8 md:px-14 py-16 text-center relative overflow-hidden shadow-2xl">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#10B981]/20 blur-2xl" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full border border-white/10" />
          <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full border border-white/15" />
          {s.heading && <h2 className="relative font-serif-site text-white text-3xl md:text-4xl font-extrabold tracking-tight">{s.heading}</h2>}
          {s.text && <p className="relative mt-4 text-slate-300 max-w-2xl mx-auto">{s.text}</p>}
          <div className="relative flex flex-wrap items-center justify-center">
            <Buttons buttons={s.buttons} testid={testid} center />
          </div>
        </div>
      </div>
    </section>
  );
}

function GallerySection({ s, testid }) {
  const images = s.images || [];
  if (!images.length) return null;
  return (
    <section className={`py-20 ${toneCls(s)}`} data-testid={testid}>
      <div className="container-x">
        <SectionHead eyebrow={s.eyebrow} heading={s.heading} subtitle={s.subtitle} centered dark={darkTone(s)} />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {images.map((src, i) => (
            <img key={`${src}-${i}`} src={src} alt="" className="w-full h-64 object-cover rounded-2xl shadow-md hover:shadow-xl transition-shadow" />
          ))}
        </div>
      </div>
    </section>
  );
}

const contactInputCls = "w-full h-12 rounded-xl border border-slate-700 bg-[#1A232E] px-4 text-sm text-white placeholder-slate-400 focus:outline-none focus:border-[#00D2C2] focus:ring-2 focus:ring-[#00D2C2]/30";

function ContactBlock({ s, contact, testid }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sendState, setSendState] = useState({ sending: false, sent: false, error: "" });
  const email = contact.email || "info@waleseventmedical.co.uk";
  const send = async (e) => {
    e.preventDefault();
    setSendState({ sending: true, sent: false, error: "" });
    try {
      const r = await fetch(`${BACKEND}/api/public/site-contact`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Could not send right now — please email us directly instead.");
      setSendState({ sending: false, sent: true, error: "" });
    } catch (err) {
      setSendState({ sending: false, sent: false, error: err.message || "Could not send right now — please email us directly instead." });
    }
  };
  return (
    <section className={`py-20 ${toneCls(s)}`} data-testid={testid}>
      <div className="container-x grid lg:grid-cols-2 gap-14">
        <div className="reveal">
          <SectionHead eyebrow={s.eyebrow} heading={s.heading} />
          {(s.paragraphs || []).filter(Boolean).map((p, i) => (
            <p key={i} className={`${i === 0 ? "mt-6" : "mt-4"} text-slate-300 leading-relaxed`}>{p}</p>
          ))}
          <div className="mt-8 space-y-4">
            <a href={`mailto:${email}`} className="flex items-center gap-3 text-white hover:text-[#00D2C2] transition-colors" data-testid="contact-email">
              <span className="inline-flex w-11 h-11 rounded-xl bg-[#00D2C2]/15 text-[#00D2C2] items-center justify-center"><Mail size={20} /></span>
              <span className="font-semibold">{email}</span>
            </a>
            {contact.phone && (
              <div className="flex items-center gap-3 text-white">
                <span className="inline-flex w-11 h-11 rounded-xl bg-[#00D2C2]/15 text-[#00D2C2] items-center justify-center"><Phone size={20} /></span>
                <span className="font-semibold">{contact.phone}</span>
              </div>
            )}
            {contact.location && (
              <div className="flex items-center gap-3 text-white">
                <span className="inline-flex w-11 h-11 rounded-xl bg-[#00D2C2]/15 text-[#00D2C2] items-center justify-center"><MapPin size={20} /></span>
                <span className="font-semibold">{contact.location}</span>
              </div>
            )}
          </div>
          <p className="mt-8 text-sm text-slate-400">
            <Link to="/intake" className="text-[#00D2C2] font-semibold hover:underline" data-testid="contact-quote-link">Prefer our quote form?</Link>
          </p>
        </div>
        <div className="reveal reveal-delay-1">
          {sendState.sent ? (
            <div className="card-soft p-8 text-center" data-testid="contact-sent">
              <span className="inline-flex w-14 h-14 rounded-full bg-[#10B981]/15 text-[#10B981] items-center justify-center"><Check size={28} /></span>
              <h3 className="mt-4 font-serif-site text-2xl font-semibold text-white">Message sent</h3>
              <p className="mt-2 text-sm text-slate-300">Thanks {form.name.split(" ")[0]} — it's landed in our inbox and we'll be in touch shortly.</p>
              <button type="button" onClick={() => { setForm({ name: "", email: "", phone: "", message: "" }); setSendState({ sending: false, sent: false, error: "" }); }}
                className="mt-6 text-sm font-semibold text-[#00D2C2] hover:underline" data-testid="contact-send-another">Send another message</button>
            </div>
          ) : (
          <form onSubmit={send} className="card-soft p-8" data-testid="contact-form">
            <h3 className="font-serif-site text-2xl font-semibold text-white">{s.form_title || "Send us a message"}</h3>
            <div className="mt-6 space-y-4">
              <label className="block">
                <span className="text-xs font-semibold text-slate-300 block mb-1.5">Name *</span>
                <input required className={contactInputCls} value={form.name} data-testid="contact-name"
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-300 block mb-1.5">Email *</span>
                <input required type="email" className={contactInputCls} value={form.email} data-testid="contact-email-input"
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-300 block mb-1.5">Phone (optional)</span>
                <input type="tel" className={contactInputCls} value={form.phone} data-testid="contact-phone-input"
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-300 block mb-1.5">How can we help you? *</span>
                <textarea required rows={5} className="w-full rounded-xl border border-slate-700 bg-[#1A232E] px-4 py-3 text-sm text-white placeholder-slate-400 focus:outline-none focus:border-[#00D2C2] focus:ring-2 focus:ring-[#00D2C2]/30"
                  value={form.message} data-testid="contact-message"
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} />
              </label>
              {sendState.error && <p className="text-sm text-[#FF7A70] font-semibold" data-testid="contact-error">{sendState.error}</p>}
              <button type="submit" disabled={sendState.sending} className="btn btn-primary w-full justify-center disabled:opacity-60" data-testid="contact-send">
                {sendState.sending ? "Sending…" : "Send Message"}
              </button>
              {s.form_note && <p className="text-xs text-slate-400 text-center">{s.form_note}</p>}
            </div>
          </form>
          )}
        </div>
      </div>
    </section>
  );
}

function BlogFeedSection({ s, testid }) {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    fetch(`${BACKEND}/api/site/blog`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setPosts(Array.isArray(d) ? d.slice(0, s.count || 3) : []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.count]);
  if (!posts.length) return null;
  return (
    <section className={`py-20 md:py-24 ${toneCls(s)}`} data-testid={testid}>
      <div className="container-x">
        <SectionHead eyebrow={s.eyebrow} heading={s.heading} subtitle={s.subtitle} centered dark />
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {posts.map((p, i) => (
            <Link key={p.id} to={`/blog/${p.slug}`} className="group card-soft overflow-hidden hover:shadow-xl transition-shadow duration-300" data-testid={`${testid}-post-${i}`}>
              {p.cover_image && (
                <div className="h-44 overflow-hidden">
                  <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
              )}
              <div className="p-6">
                <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00D2C2]">
                  <CalendarDays size={14} /> {fmtDate(p.published_at)}
                </p>
                <h3 className="mt-2 font-serif-site text-lg font-bold text-white group-hover:text-[#00D2C2] transition-colors">{p.title}</h3>
                {p.excerpt && <p className="mt-2 text-sm text-slate-300 leading-relaxed line-clamp-3">{p.excerpt}</p>}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function PageHeroSection({ s, testid }) {
  return (
    <div data-testid={testid}>
      <PageHero title={s.title} subtitle={s.subtitle} image={s.image || "/site-assets/hero4.jpg"} />
    </div>
  );
}

const RENDERERS = {
  hero: HeroSection,
  page_hero: PageHeroSection,
  trust_bar: TrustBar,
  services_grid: ServicesGrid,
  text: TextSection,
  text_image: TextImageSection,
  features: FeaturesSection,
  chips: ChipsSection,
  checklist: ChecklistSection,
  columns: ColumnsSection,
  reviews: ReviewsSection,
  calculator_promo: CalculatorPromo,
  cta: CtaSection,
  gallery: GallerySection,
  contact_block: ContactBlock,
  blog_feed: BlogFeedSection,
  kit_grid: KitGridSection,
  price_cards: PriceCardsSection,
  kit_detail: KitDetailSection,
  partners: PartnersSection,
};

export default function SiteSections({ sections = [], services = [], contact = {} }) {
  return (
    <>
      {(sections || []).map((s, i) => {
        const R = RENDERERS[s.type];
        if (!R) return null;
        return <R key={s.id || i} s={s} services={services} contact={contact} testid={`section-${s.type}-${i}`} />;
      })}
    </>
  );
}
